(() => {
  let isOpen = false;
  let isUpdating = false;
  let lastFocusedElement = null;
  let backgroundState = [];
  let refreshSequence = 0;

  const getRoot = () => document.querySelector('[data-cart-drawer-root]');
  const getPanel = () => getRoot()?.querySelector('[data-cart-drawer-panel]');
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const initializeRoot = () => {
    const root = getRoot();
    if (!root) return null;
    root.hidden = false;
    root.dataset.cartDrawerReady = 'true';
    return root;
  };

  const setBackgroundInert = (shouldBeInert) => {
    const root = getRoot();
    if (!root) return;

    if (shouldBeInert) {
      const drawerSection = root.closest('.shopify-section') || root;
      backgroundState = [...document.body.children]
        .filter((element) => element !== drawerSection && !element.contains(root) && !['SCRIPT', 'STYLE'].includes(element.tagName))
        .map((element) => ({ element, wasInert: element.inert }));
      backgroundState.forEach(({ element }) => { element.inert = true; });
      return;
    }

    backgroundState.forEach(({ element, wasInert }) => { element.inert = wasInert; });
    backgroundState = [];
  };

  const syncOpenState = (focusClose = false) => {
    const root = initializeRoot();
    const panel = getPanel();
    if (!root || !panel) return;

    root.classList.toggle('is-open', isOpen);
    root.setAttribute('aria-hidden', String(!isOpen));
    panel.toggleAttribute('inert', !isOpen);
    document.body.classList.toggle('fv-cart-drawer-open', isOpen);
    document.querySelectorAll('[data-cart-drawer-open]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(isOpen));
    });

    if (isOpen && focusClose) {
      requestAnimationFrame(() => root.querySelector('button[data-cart-drawer-close]')?.focus());
    }
  };

  const openDrawer = (trigger = null) => {
    if (isOpen) return;
    lastFocusedElement = trigger || document.activeElement;
    isOpen = true;
    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.body.style.setProperty('--fv-cart-scrollbar-offset', `${scrollbarWidth}px`);
    setBackgroundInert(true);
    syncOpenState(true);
  };

  const closeDrawer = () => {
    if (!isOpen) return;
    isOpen = false;
    syncOpenState();
    setBackgroundInert(false);
    document.body.style.removeProperty('--fv-cart-scrollbar-offset');
    if (lastFocusedElement instanceof HTMLElement && lastFocusedElement.isConnected) {
      lastFocusedElement.focus();
    }
  };

  const setBusy = (busy) => {
    isUpdating = busy;
    const root = getRoot();
    if (!root) return;
    root.classList.toggle('is-updating', busy);
    root.querySelector('[data-cart-drawer-content]')?.setAttribute('aria-busy', String(busy));
  };

  const showError = (message) => {
    const root = initializeRoot();
    if (!root) return;
    const error = root.querySelector('[data-cart-drawer-error]');
    if (!error) return;
    error.textContent = message || 'The bag could not be updated. Please try again.';
    error.hidden = false;
  };

  const clearError = () => {
    const error = getRoot()?.querySelector('[data-cart-drawer-error]');
    if (!error) return;
    error.textContent = '';
    error.hidden = true;
  };

  const announce = (message) => {
    const status = getRoot()?.querySelector('[data-cart-drawer-status]');
    if (!status) return;
    status.textContent = '';
    requestAnimationFrame(() => { status.textContent = message; });
  };

  const restoreDrawerFocus = (target) => {
    if (!isOpen || !target) return;
    const root = getRoot();
    if (!root) return;

    let element = null;
    if (target.type === 'line-action') {
      element = [...root.querySelectorAll('[data-line-key]')].find((candidate) => (
        candidate.dataset.lineKey === target.lineKey
        && (target.attribute === 'data-cart-remove'
          ? candidate.hasAttribute('data-cart-remove')
          : candidate.dataset.cartQuantityChange === target.change)
      ));
    }
    if (!element) element = root.querySelector('button[data-cart-drawer-close]');
    element?.focus();
  };

  const syncCartCount = (count) => {
    document.querySelectorAll('[data-cart-drawer-open]').forEach((trigger) => {
      let badge = trigger.querySelector('[data-cart-count]');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'fv-cart-count';
        badge.dataset.cartCount = '';
        trigger.append(badge);
      }
      badge.textContent = String(count);
      badge.hidden = count < 1;
      trigger.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
    });
  };

  const refreshDrawer = async (knownItemCount = null, options = {}) => {
    const root = initializeRoot();
    if (!root) throw new Error('Cart drawer is unavailable.');
    const requestId = ++refreshSequence;

    const url = new URL(root.dataset.cartUrl || '/cart', window.location.origin);
    url.searchParams.set('section_id', root.dataset.sectionId || 'cart-drawer');
    url.searchParams.set('_', String(Date.now()));

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('The bag could not be refreshed.');

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const freshRoot = parsed.querySelector('[data-cart-drawer-root]');
    if (!freshRoot) throw new Error('The bag could not be refreshed.');
    if (requestId !== refreshSequence) return false;

    root.innerHTML = freshRoot.innerHTML;
    root.dataset.itemCount = freshRoot.dataset.itemCount || String(knownItemCount ?? 0);
    const itemCount = knownItemCount ?? Number(root.dataset.itemCount || 0);
    syncCartCount(itemCount);
    syncOpenState();
    restoreDrawerFocus(options.focusTarget);
    if (options.announcement) announce(options.announcement);
    return true;
  };

  const changeLine = async (key, quantity, focusTarget = null) => {
    const root = initializeRoot();
    if (!root || isUpdating) return;

    clearError();
    setBusy(true);
    try {
      const response = await fetch(root.dataset.cartChangeUrl || '/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
      });
      const cart = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(cart.description || cart.message || 'The bag could not be updated.');
      await refreshDrawer(cart.item_count, {
        focusTarget,
        announcement: quantity > 0 ? `Quantity updated. ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in your bag.` : `Item removed. ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in your bag.`
      });
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const addProduct = async (form, submitButton) => {
    const root = initializeRoot();
    if (!root || isUpdating) return;

    clearError();
    setBusy(true);
    const originalLabel = submitButton?.textContent;
    const originalDisabled = submitButton?.disabled;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Adding…';
    }

    try {
      const response = await fetch(root.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.description || result.message || 'This object could not be added.');
      await refreshDrawer();
      openDrawer(submitButton);
      document.dispatchEvent(new CustomEvent('fanniverse:cart-updated', { detail: result }));
    } catch (error) {
      openDrawer(submitButton);
      showError(error.message);
    } finally {
      setBusy(false);
      if (submitButton && submitButton.isConnected) {
        submitButton.disabled = Boolean(originalDisabled);
        submitButton.textContent = originalLabel;
      }
    }
  };

  const openAndRefresh = async (trigger) => {
    openDrawer(trigger);
    if (isUpdating) return;
    clearError();
    setBusy(true);
    try {
      await refreshDrawer(null, { focusTarget: { type: 'close' } });
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const openTrigger = target.closest('[data-cart-drawer-open]');
    if (openTrigger && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      openAndRefresh(openTrigger);
      return;
    }

    if (target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const quantityButton = target.closest('[data-cart-quantity-change]');
    if (quantityButton) {
      event.preventDefault();
      const change = Number(quantityButton.dataset.cartQuantityChange || 0);
      const current = Number(quantityButton.dataset.currentQuantity || 0);
      changeLine(quantityButton.dataset.lineKey, current + change, {
        type: 'line-action',
        lineKey: quantityButton.dataset.lineKey,
        change: quantityButton.dataset.cartQuantityChange,
        attribute: 'data-cart-quantity-change'
      });
      return;
    }

    const removeButton = target.closest('[data-cart-remove]');
    if (removeButton) {
      event.preventDefault();
      changeLine(removeButton.dataset.lineKey, 0, {
        type: 'line-action',
        lineKey: removeButton.dataset.lineKey,
        change: null,
        attribute: 'data-cart-remove'
      });
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form');
    if (!form || form.dataset.ajaxCart === 'false') return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;

    const submitButton = event.submitter || form.querySelector('[type="submit"]');
    if (submitButton?.disabled || (submitButton?.name && submitButton.name !== 'add')) return;
    event.preventDefault();
    addProduct(form, submitButton);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (!isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;

    const panel = getPanel();
    if (!panel) return;
    const focusable = [...panel.querySelectorAll(focusableSelector)].filter((element) => !element.hidden && element.getClientRects().length > 0);
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('DOMContentLoaded', initializeRoot);
  document.addEventListener('shopify:section:load', initializeRoot);
})();
